import React, { useMemo, useState, useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { Search, Plus, Trash2, Loader2, Store, User, FileText, Download, ReceiptText, ChevronLeft, ChevronDown, Lock } from "lucide-react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import AltosLoginDialog from "@/components/AltosLoginDialog";
import { api, formatINR } from "@/lib/api";
import { ALTOS_CATALOG, ALTOS_CATEGORIES } from "@/data/altosCatalog";
import { useStore } from "@/context/StoreContext";
import { toast } from "sonner";

const lineTotal = (l) => {
  const gross = (Number(l.mrp) || 0) * (Number(l.qty) || 0);
  let disc = 0;
  if (l.discount_type === "percent") disc = gross * (Math.min(Number(l.discount_value) || 0, 100) / 100);
  else if (l.discount_type === "rupees") disc = Number(l.discount_value) || 0;
  disc = Math.min(Math.max(disc, 0), gross);
  return { gross, disc, total: gross - disc };
};

function ProfileForm({ altosId, altosName, initial, onSaved }) {
  const [type, setType] = useState(initial?.type || "centre");
  const [name, setName] = useState(initial?.name || "");
  const [centreId, setCentreId] = useState(initial?.centre_id || "");
  const [address, setAddress] = useState(initial?.address || "");
  const [mobile, setMobile] = useState(initial?.mobile || "");
  const [busy, setBusy] = useState(false);
  const cls = "w-full h-11 rounded-lg border border-line px-3 text-sm mt-1 focus:outline-none focus:border-brand";

  const save = async () => {
    if (!name.trim()) return toast.error("Enter a name");
    if (!mobile.trim()) return toast.error("Enter a mobile number");
    if (type === "centre" && !centreId.trim()) return toast.error("Centre ID is required");
    setBusy(true);
    try {
      const saved = await api.saveBillingProfile({
        altos_id: altosId, altos_name: altosName || "", type,
        name: name.trim(), centre_id: centreId.trim(), address: address.trim(), mobile: mobile.trim(),
      });
      toast.success("Profile saved");
      onSaved(saved);
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };

  return (
    <div className="max-w-lg rounded-2xl border border-line p-5" data-testid="billing-profile-form">
      <h2 className="font-display text-2xl text-ink mb-1">{initial ? "Edit your profile" : "Register to start billing"}</h2>
      <p className="text-xs text-muted2 mb-4">Choose how you sell. This appears on every bill you generate.</p>
      <div className="flex gap-3 mb-4">
        <button onClick={() => setType("centre")} data-testid="profile-type-centre" className={`flex-1 h-16 rounded-xl border flex flex-col items-center justify-center gap-1 text-sm font-semibold transition-colors ${type === "centre" ? "border-brand bg-mist text-brand" : "border-line text-ink"}`}>
          <Store size={18} /> Centre
        </button>
        <button onClick={() => setType("direct_seller")} data-testid="profile-type-seller" className={`flex-1 h-16 rounded-xl border flex flex-col items-center justify-center gap-1 text-sm font-semibold transition-colors ${type === "direct_seller" ? "border-brand bg-mist text-brand" : "border-line text-ink"}`}>
          <User size={18} /> Direct Seller
        </button>
      </div>
      <label className="block mb-3"><span className="text-xs font-semibold text-muted2">{type === "centre" ? "Centre name" : "Seller name"} *</span>
        <input value={name} onChange={(e) => setName(e.target.value)} className={cls} data-testid="profile-name" /></label>
      {type === "centre" && (
        <>
          <label className="block mb-3"><span className="text-xs font-semibold text-muted2">Centre ID *</span>
            <input value={centreId} onChange={(e) => setCentreId(e.target.value)} className={cls} data-testid="profile-centre-id" /></label>
          <label className="block mb-3"><span className="text-xs font-semibold text-muted2">Address</span>
            <textarea value={address} onChange={(e) => setAddress(e.target.value)} rows={2} className={`${cls} h-auto py-2`} data-testid="profile-address" /></label>
        </>
      )}
      <label className="block mb-4"><span className="text-xs font-semibold text-muted2">Mobile number *</span>
        <input value={mobile} onChange={(e) => setMobile(e.target.value)} className={cls} data-testid="profile-mobile" /></label>
      <button onClick={save} disabled={busy} className="h-11 px-6 rounded-full bg-brand text-white font-semibold flex items-center gap-2 disabled:opacity-60" data-testid="profile-save">
        {busy ? <Loader2 className="animate-spin" size={18} /> : "Save profile"}
      </button>
    </div>
  );
}

export default function Billing() {
  const { verified, altosId, altosName } = useStore();
  const qc = useQueryClient();
  const [loginOpen, setLoginOpen] = useState(false);
  const [editProfile, setEditProfile] = useState(false);

  const { data: profile, isLoading: profileLoading } = useQuery({
    queryKey: ["billing-profile", altosId],
    queryFn: () => api.getBillingProfile(altosId),
    enabled: verified && !!altosId,
  });
  const { data: bills = [] } = useQuery({
    queryKey: ["billing-bills", altosId],
    queryFn: () => api.listBills(altosId),
    enabled: verified && !!altosId,
  });
  const { data: customProducts = [] } = useQuery({
    queryKey: ["billing-products", altosId],
    queryFn: () => api.listBillingProducts(altosId),
    enabled: verified && !!altosId,
  });

  const [q, setQ] = useState("");
  const [cat, setCat] = useState("ALL");
  const [pickerOpen, setPickerOpen] = useState(false);
  const [showAddForm, setShowAddForm] = useState(false);
  const [np, setNp] = useState({ name: "", unit: "", mrp: "" });
  const [lines, setLines] = useState([]);
  const [custName, setCustName] = useState("");
  const [custMobile, setCustMobile] = useState("");
  const [saving, setSaving] = useState(false);

  const allProducts = useMemo(() => {
    const custom = (customProducts || []).map((c) => ({ code: c.code, name: c.name, unit: c.unit || "", mrp: c.mrp, category: "MY PRODUCTS", custom: true }));
    return [...custom, ...ALTOS_CATALOG];
  }, [customProducts]);

  const filtered = useMemo(() => {
    const query = q.trim().toLowerCase();
    return allProducts.filter((p) =>
      (cat === "ALL" || p.category === cat) &&
      (!query || p.name.toLowerCase().includes(query) || String(p.code).toLowerCase().includes(query))
    ).slice(0, 60);
  }, [q, cat, allProducts]);

  const addNewProduct = async () => {
    if (!np.name.trim()) return toast.error("Enter product name");
    if (np.mrp === "" || Number(np.mrp) < 0) return toast.error("Enter a valid MRP");
    try {
      const created = await api.addBillingProduct({ altos_id: altosId, name: np.name.trim(), unit: np.unit.trim(), mrp: Number(np.mrp) });
      toast.success("Product added");
      setNp({ name: "", unit: "", mrp: "" });
      setShowAddForm(false);
      qc.invalidateQueries({ queryKey: ["billing-products", altosId] });
      addLine({ code: created.code, name: created.name, unit: created.unit || "", mrp: created.mrp });
      setPickerOpen(false);
    } catch (e) { toast.error(e.message); }
  };

  const addLine = (p) => {
    setLines((prev) => {
      const f = prev.find((l) => l.code === p.code);
      if (f) return prev.map((l) => l.code === p.code ? { ...l, qty: l.qty + 1 } : l);
      return [...prev, { code: p.code, name: p.name, unit: p.unit, mrp: p.mrp, qty: 1, discount_type: "none", discount_value: 0 }];
    });
  };
  const patch = (code, k, v) => setLines((prev) => prev.map((l) => l.code === code ? { ...l, [k]: v } : l));
  const removeLine = (code) => setLines((prev) => prev.filter((l) => l.code !== code));

  const totals = useMemo(() => {
    let mrp = 0, disc = 0;
    lines.forEach((l) => { const t = lineTotal(l); mrp += t.gross; disc += t.disc; });
    return { mrp, disc, grand: mrp - disc };
  }, [lines]);

  const generate = async () => {
    if (!custName.trim()) return toast.error("Customer name is required");
    if (!custMobile.trim()) return toast.error("Customer mobile is required");
    if (lines.length === 0) return toast.error("Add at least one product");
    setSaving(true);
    try {
      const bill = await api.createBill({
        altos_id: altosId,
        customer_name: custName.trim(),
        customer_mobile: custMobile.trim(),
        items: lines.map((l) => ({
          code: l.code, name: l.name, unit: l.unit, mrp: Number(l.mrp) || 0,
          qty: Number(l.qty) || 1, discount_type: l.discount_type, discount_value: Number(l.discount_value) || 0,
        })),
      });
      toast.success("Bill generated & saved");
      setLines([]); setCustName(""); setCustMobile("");
      qc.invalidateQueries({ queryKey: ["billing-bills", altosId] });
      window.open(api.billPdfUrl(bill.id), "_blank");
    } catch (e) { toast.error(e.message); } finally { setSaving(false); }
  };

  if (!verified) {
    return (
      <div className="min-h-screen bg-paper flex flex-col">
        <Header />
        <main className="flex-1 max-w-3xl mx-auto w-full px-4 py-20 text-center">
          <Lock className="mx-auto text-brand mb-4" size={40} />
          <h1 className="font-display text-4xl text-ink">Retail Billing</h1>
          <p className="text-muted2 mt-3 mb-6">This billing tool is exclusively for Altos ID holders. Please log in with your Altos ID to continue.</p>
          <button onClick={() => setLoginOpen(true)} data-testid="billing-login-cta" className="h-12 px-8 rounded-full bg-brand text-white font-semibold">Log in with Altos ID</button>
        </main>
        <Footer />
        <AltosLoginDialog open={loginOpen} onClose={() => setLoginOpen(false)} />
      </div>
    );
  }

  const hasProfile = profile && profile.name;

  return (
    <div className="min-h-screen bg-paper flex flex-col">
      <Header />
      <main className="flex-1 max-w-6xl mx-auto w-full px-4 py-8">
        <Link to="/" className="flex items-center gap-1 text-sm text-muted2 hover:text-brand mb-4"><ChevronLeft size={16} /> Back to store</Link>
        <div className="flex items-center gap-3 mb-6">
          <ReceiptText className="text-brand" size={28} />
          <div>
            <h1 className="font-display text-4xl text-ink">Retail Billing</h1>
            <p className="text-sm text-muted2">Bill your customers on MRP — Altos World Online billing software.</p>
          </div>
        </div>

        {profileLoading ? (
          <p className="text-muted2">Loading…</p>
        ) : (!hasProfile || editProfile) ? (
          <ProfileForm altosId={altosId} altosName={altosName} initial={hasProfile ? profile : null}
            onSaved={() => { qc.invalidateQueries({ queryKey: ["billing-profile", altosId] }); setEditProfile(false); }} />
        ) : (
          <>
            <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-line bg-mist/40 p-4 mb-6" data-testid="active-profile">
              <div className="flex items-center gap-3">
                {profile.type === "centre" ? <Store className="text-brand" size={20} /> : <User className="text-brand" size={20} />}
                <div>
                  <p className="font-semibold text-ink">{profile.name} <span className="text-xs font-normal text-muted2">· {profile.type === "centre" ? "Centre" : "Direct Seller"}{profile.centre_id ? ` · ID ${profile.centre_id}` : ""}</span></p>
                  <p className="text-xs text-muted2">Mobile: {profile.mobile}</p>
                </div>
              </div>
              <button onClick={() => setEditProfile(true)} className="text-sm font-semibold text-brand" data-testid="edit-profile-btn">Edit profile</button>
            </div>

            <div className="grid lg:grid-cols-[1fr_380px] gap-6">
              {/* Product picker (dropdown) */}
              <div className="rounded-2xl border border-line p-4">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="font-display text-xl text-ink">Add products</h3>
                  <button onClick={() => { setShowAddForm((v) => !v); setPickerOpen(false); }} className="text-sm font-semibold text-brand flex items-center gap-1" data-testid="toggle-add-product"><Plus size={15} /> New product</button>
                </div>

                {showAddForm && (
                  <div className="rounded-xl border border-brand/40 bg-mist/40 p-3 mb-3" data-testid="add-product-form">
                    <p className="text-xs font-semibold text-muted2 mb-2">Add a product that isn't in the Altos list</p>
                    <div className="grid grid-cols-2 gap-2">
                      <input value={np.name} onChange={(e) => setNp({ ...np, name: e.target.value })} placeholder="Product name *" className="h-9 rounded border border-line px-2 text-sm col-span-2 focus:outline-none focus:border-brand" data-testid="np-name" />
                      <input value={np.unit} onChange={(e) => setNp({ ...np, unit: e.target.value })} placeholder="Unit (e.g. 500 ml)" className="h-9 rounded border border-line px-2 text-sm focus:outline-none focus:border-brand" data-testid="np-unit" />
                      <input type="number" value={np.mrp} onChange={(e) => setNp({ ...np, mrp: e.target.value })} placeholder="MRP ₹ *" className="h-9 rounded border border-line px-2 text-sm focus:outline-none focus:border-brand" data-testid="np-mrp" />
                    </div>
                    <button onClick={addNewProduct} className="mt-2 h-9 px-4 rounded-full bg-brand text-white text-sm font-semibold" data-testid="np-save">Add &amp; insert</button>
                  </div>
                )}

                <button onClick={() => setPickerOpen((o) => !o)} className="w-full h-11 rounded-lg border border-line px-3 flex items-center justify-between text-sm text-muted2 hover:border-brand focus:outline-none focus:border-brand" data-testid="product-dropdown-toggle">
                  <span>Select a product to add…</span>
                  <ChevronDown size={16} className={pickerOpen ? "rotate-180 transition-transform" : "transition-transform"} />
                </button>

                {pickerOpen && (
                  <div className="mt-2 rounded-xl border border-line overflow-hidden" data-testid="product-dropdown-panel">
                    <div className="flex gap-2 p-2 border-b border-line bg-mist/30">
                      <div className="relative flex-1">
                        <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted2" size={15} />
                        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name or code…" className="w-full h-9 rounded border border-line pl-8 pr-2 text-sm focus:outline-none focus:border-brand" data-testid="billing-search" autoFocus />
                      </div>
                      <select value={cat} onChange={(e) => setCat(e.target.value)} className="h-9 rounded border border-line px-1 text-sm focus:outline-none focus:border-brand" data-testid="billing-category">
                        <option value="ALL">All</option>
                        {customProducts.length > 0 && <option value="MY PRODUCTS">My Products</option>}
                        {ALTOS_CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
                      </select>
                    </div>
                    <div className="max-h-[300px] overflow-y-auto divide-y divide-line">
                      {filtered.map((p) => (
                        <button key={p.code} onClick={() => { addLine(p); setPickerOpen(false); setQ(""); }} data-testid={`catalog-add-${p.code}`} className="w-full flex items-center gap-3 py-2.5 px-3 text-left hover:bg-mist/50 transition-colors">
                          <Plus size={16} className="text-brand shrink-0" />
                          <span className="flex-1 min-w-0">
                            <span className="block text-sm text-ink truncate">{p.name}{p.custom && <span className="ml-1 text-[10px] text-brand font-semibold">(yours)</span>}</span>
                            <span className="block text-xs text-muted2">{p.code} · {p.unit}</span>
                          </span>
                          <span className="text-sm font-semibold text-ink shrink-0">{formatINR(p.mrp)}</span>
                        </button>
                      ))}
                      {filtered.length === 0 && <p className="text-sm text-muted2 py-6 text-center">No products match.</p>}
                    </div>
                  </div>
                )}
              </div>

              {/* Bill panel */}
              <div className="rounded-2xl border border-line p-4 h-fit lg:sticky lg:top-4">
                <h3 className="font-display text-xl text-ink mb-3">Current bill</h3>
                <div className="grid grid-cols-2 gap-2 mb-3">
                  <input value={custName} onChange={(e) => setCustName(e.target.value)} placeholder="Customer name *" className="h-10 rounded-lg border border-line px-3 text-sm focus:outline-none focus:border-brand" data-testid="cust-name" />
                  <input value={custMobile} onChange={(e) => setCustMobile(e.target.value)} placeholder="Customer mobile *" className="h-10 rounded-lg border border-line px-3 text-sm focus:outline-none focus:border-brand" data-testid="cust-mobile" />
                </div>

                {lines.length === 0 ? (
                  <p className="text-sm text-muted2 py-6 text-center border border-dashed border-line rounded-lg">Add products from the left to build the bill.</p>
                ) : (
                  <div className="space-y-3 max-h-[300px] overflow-y-auto pr-1">
                    {lines.map((l) => {
                      const t = lineTotal(l);
                      return (
                        <div key={l.code} className="rounded-lg border border-line p-2.5" data-testid={`bill-line-${l.code}`}>
                          <div className="flex items-start justify-between gap-2">
                            <span className="text-sm text-ink flex-1">{l.name}</span>
                            <button onClick={() => removeLine(l.code)} className="text-muted2 hover:text-err" data-testid={`bill-remove-${l.code}`}><Trash2 size={14} /></button>
                          </div>
                          <div className="grid grid-cols-2 gap-2 mt-2">
                            <label className="text-[11px] text-muted2">MRP ₹
                              <input type="number" value={l.mrp} onChange={(e) => patch(l.code, "mrp", e.target.value)} className="w-full h-8 rounded border border-line px-2 text-sm mt-0.5" data-testid={`bill-mrp-${l.code}`} />
                            </label>
                            <label className="text-[11px] text-muted2">Qty
                              <input type="number" min="1" value={l.qty} onChange={(e) => patch(l.code, "qty", Math.max(1, parseInt(e.target.value || "1", 10)))} className="w-full h-8 rounded border border-line px-2 text-sm mt-0.5" data-testid={`bill-qty-${l.code}`} />
                            </label>
                            <label className="text-[11px] text-muted2">Discount type
                              <select value={l.discount_type} onChange={(e) => patch(l.code, "discount_type", e.target.value)} className="w-full h-8 rounded border border-line px-1 text-sm mt-0.5" data-testid={`bill-disc-type-${l.code}`}>
                                <option value="none">None</option>
                                <option value="percent">%</option>
                                <option value="rupees">₹</option>
                              </select>
                            </label>
                            <label className="text-[11px] text-muted2">Discount value
                              <input type="number" min="0" value={l.discount_value} disabled={l.discount_type === "none"} onChange={(e) => patch(l.code, "discount_value", e.target.value)} className="w-full h-8 rounded border border-line px-2 text-sm mt-0.5 disabled:bg-mist/50" data-testid={`bill-disc-val-${l.code}`} />
                            </label>
                          </div>
                          <p className="text-right text-sm font-semibold text-ink mt-1">{formatINR(t.total)}</p>
                        </div>
                      );
                    })}
                  </div>
                )}

                <div className="border-t border-line mt-3 pt-3 space-y-1 text-sm">
                  <div className="flex justify-between text-muted2"><span>Total MRP</span><span>{formatINR(totals.mrp)}</span></div>
                  <div className="flex justify-between text-muted2"><span>Total discount</span><span>- {formatINR(totals.disc)}</span></div>
                  <div className="flex justify-between font-bold text-ink text-base"><span>Grand total</span><span data-testid="bill-grand-total">{formatINR(totals.grand)}</span></div>
                </div>
                <button onClick={generate} disabled={saving} className="w-full h-12 mt-4 rounded-full bg-brand text-white font-semibold flex items-center justify-center gap-2 disabled:opacity-60" data-testid="generate-bill">
                  {saving ? <Loader2 className="animate-spin" size={18} /> : <><FileText size={18} /> Generate & Download PDF</>}
                </button>
              </div>
            </div>

            {/* Saved bills */}
            {bills.length > 0 && (
              <div className="mt-10">
                <h3 className="font-display text-2xl text-ink mb-3">Saved bills</h3>
                <div className="rounded-2xl border border-line divide-y divide-line" data-testid="saved-bills">
                  {bills.map((b) => (
                    <div key={b.id} className="flex flex-wrap items-center justify-between gap-2 p-3" data-testid={`saved-bill-${b.id}`}>
                      <div>
                        <p className="text-sm font-semibold text-ink">{b.customer_name} <span className="text-xs font-normal text-muted2">· {b.customer_mobile}</span></p>
                        <p className="text-xs text-muted2">#{b.id.slice(0, 8).toUpperCase()} · {(b.created_at || "").slice(0, 10)} · {b.items?.length || 0} item(s)</p>
                      </div>
                      <div className="flex items-center gap-4">
                        <span className="font-semibold text-ink">{formatINR(b.grand_total)}</span>
                        <a href={api.billPdfUrl(b.id)} target="_blank" rel="noreferrer" className="text-sm font-semibold text-brand flex items-center gap-1" data-testid={`bill-download-${b.id}`}><Download size={15} /> PDF</a>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </>
        )}
      </main>
      <Footer />
    </div>
  );
}
